/**
 * Image utility functions for validating and handling image URLs
 */

/**
 * Validates if an image URL is accessible and loads successfully
 * @param {string} url - Image URL to validate
 * @returns {Promise<boolean>} - True if image loads successfully, false otherwise
 */
export const validateImageUrl = (url) => {
  return new Promise((resolve) => {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      resolve(false);
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      resolve(true);
    };
    
    img.onerror = () => {
      resolve(false);
    };
    
    // Set a timeout to prevent hanging on slow networks
    const timeout = setTimeout(() => {
      img.src = '';
      resolve(false);
    }, 5000);
    
    img.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };
    
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };
    
    img.src = url;
  });
};

/**
 * Validates multiple image URLs in parallel
 * @param {Array<{url: string, id: string}>} images - Array of image objects with url and id
 * @returns {Promise<Array<{id: string, url: string, isValid: boolean}>>} - Array with validation results
 */
export const validateMultipleImages = async (images) => {
  if (!Array.isArray(images)) {
    return [];
  }

  const validationPromises = images?.map(async (image) => {
    const isValid = await validateImageUrl(image?.url);
    return {
      id: image?.id,
      url: image?.url,
      isValid
    };
  });

  return Promise.all(validationPromises);
};

/**
 * Gets a fallback placeholder image for broken talent images
 * @returns {string} - Data URL for placeholder image or fallback path
 */
export const getTalentPlaceholder = () => {
  return '/assets/images/no_image.png';
};

/**
 * Generates initials from name for avatar placeholder
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {string} - Initials (e.g., "JD" for John Doe)
 */
export const getInitials = (firstName, lastName) => {
  const first = firstName?.trim()?.[0]?.toUpperCase() || '';
  const last = lastName?.trim()?.[0]?.toUpperCase() || '';
  return `${first}${last}`;
};

/**
 * Handles image loading errors with fallback
 * @param {Event} event - Image error event
 * @param {Function} setImageState - State setter function
 */
export const handleImageError = (event, setImageState) => {
  if (event?.target) {
    event.target.src = getTalentPlaceholder();
    if (setImageState) {
      setImageState(null);
    }
  }
};